/**
 * Webflow is global namespace available in the projects that are served on the Canvas as well as in on the published sites.
 * It provides access to Webflow owned JS code that is part of each published site and allows to support interactions as well as work of
 * some more advanced elements that can be part of the projects (sliders etc.).
 */

import type {LottieModule} from '@packages/shared/render/plugins/Animation/modules/LottieSiteModule';
import type {RiveModule} from '@packages/shared/render/plugins/Animation/modules/rive-types';
import type {SplineModule} from '@packages/shared/render/plugins/Animation/modules/spline-types';
import type {IX3SiteModule} from '@packages/systems/plugins/IX3';

type WebflowModuleName =
  | 'ix3'
  | 'ix2'
  | 'ix'
  | 'commerce'
  | 'lottie'
  | 'rive'
  | 'lightbox'
  | 'spline'
  | 'focus-visible'
  | 'dropdown'
  | 'slider';

type TypedWebflowModules = {
  lottie: LottieModule | undefined;
  ix3: IX3SiteModule | undefined;
  rive: RiveModule | undefined;
  spline: SplineModule | undefined;
};

export type WebflowSiteModules = {
  /** Allows installed site modules api access from public/js/site/webflow.js, in the site iframe */
  require: <Name extends WebflowModuleName>(
    name: Name
  ) => Name extends keyof TypedWebflowModules ? TypedWebflowModules[Name] : any;
  destroy: () => void;
  ready: any;
  env: (
    mode: 'design' | 'preview' | 'editor' | 'slug' | 'test' | 'frame'
  ) => boolean;
};

declare global {
  interface Window {
    Webflow: WebflowSiteModules;
    __wf_ix3?: boolean;
  }
}

let __siteIframeNext: HTMLIFrameElement | null = null;

const getSiteIframeNext = (): HTMLIFrameElement | null => {
  if (!__siteIframeNext) {
    if (process.env.NODE_ENV !== 'development') {
      throw new Error(
        [
          '__siteIframeNext is ' + String(__siteIframeNext) + '.',
          'Did you call `SiteIframeMigrationHelper.setCanvasNextIframe?`',
        ].join('\n')
      );
    } else {
      console.error(
        `__siteIframeNext is ${String(__siteIframeNext)}. Did you call \`SiteIframeMigrationHelper.setCanvasNextIframe?\``
      );
    }

    return null;
  }

  return __siteIframeNext;
};

export const getActiveIframe = () => __siteIframeNext;

export const getActiveWindow = (): Window | null =>
  getSiteIframeNext()?.contentWindow || null;
export const getActiveDocument = (): Document | null =>
  getSiteIframeNext()?.contentDocument || null;

export const setCanvasNextIframe = (iframe: HTMLIFrameElement | null) => {
  __siteIframeNext = iframe;
};
